import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Swal from 'sweetalert2'
import { getThemeById } from '../config/themes'
import type { TenantThemeConfig } from '../types/theme'
import DepositPageHeader from '../components/DepositPageHeader'
import * as authService from '../services/authService'
import * as walletService from '../services/walletService'
import * as depositAccountService from '../services/depositAccountService'
import {
  getPublicDepositAccounts,
  getDecimalReservationCountdownMinutes,
  type PublicDepositAccount
} from '../services/depositAccountService'
import { createDecimalReservation, cancelDecimalReservation, getReservationStatus } from '../services/decimalDepositService'
import { pendingDepositDraftService, type PendingDepositSenderAccount } from '../services/pendingDepositDraftService'
import truemoneyLogo from '@/assets/truemoney-logo.png'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernDepositTimeoutPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

/** โลโก้บัญชีฝาก (ของเว็บ) */
function getDepositAccountLogoUrl(account: PublicDepositAccount, getAssetUrl: (p: string) => string): string {
  if (account.bankIcon) return account.bankIcon
  const code = (account.bankCode || '').toUpperCase()
  const name = (account.bankName || '').toLowerCase()
  if (code === 'TRUE' || name.includes('true money')) return typeof truemoneyLogo === 'string' ? truemoneyLogo : (truemoneyLogo as unknown as string)
  return getAssetUrl(`/img/b/${code || 'KBANK'}.png`)
}

const STORAGE_KEY_PREFIX = 'decimal_reservation_'

function getStorageKey(tenantId: string) {
  return `${STORAGE_KEY_PREFIX}${tenantId}`
}

function getStoredReservation(tenantId: string): {
  reservationId: string
  expiresAt: string
  amount: number
  requestedAmount?: number
  decimalSuffix?: number
} | null {
  try {
    const raw = sessionStorage.getItem(getStorageKey(tenantId))
    if (!raw) return null
    const data = JSON.parse(raw) as {
      reservationId?: string
      expiresAt?: string
      amount?: number
      requestedAmount?: number
      decimalSuffix?: number
    }
    if (data?.reservationId && data?.expiresAt) {
      return data as {
        reservationId: string
        expiresAt: string
        amount: number
        requestedAmount?: number
        decimalSuffix?: number
      }
    }
    return null
  } catch {
    return null
  }
}

function setStoredReservation(
  tenantId: string,
  data: { reservationId: string; expiresAt: string; amount: number; requestedAmount?: number; decimalSuffix?: number }
) {
  sessionStorage.setItem(getStorageKey(tenantId), JSON.stringify(data))
}

function clearStoredReservation(tenantId: string) {
  sessionStorage.removeItem(getStorageKey(tenantId))
}

const panelStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, rgba(8,15,30,0.96) 0%, rgba(15,23,42,0.94) 100%)',
  boxShadow: '0 18px 38px rgba(15, 23, 42, 0.24)',
}

const innerSurfaceStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(241,245,249,0.96) 100%)',
  boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
}

const valueCardStyle: React.CSSProperties = {
  ...innerSurfaceStyle,
  borderRadius: 22,
  padding: '1rem 1rem',
}

const lightSurfaceLabelStyle: React.CSSProperties = {
  color: '#64748b',
  fontWeight: 600,
}

const lightSurfaceMutedTextStyle: React.CSSProperties = {
  color: '#475569',
  fontWeight: 500,
}

const lightSurfaceHeadingStyle: React.CSSProperties = {
  color: '#0f172a',
}

const stepCardStyle = (active: boolean): React.CSSProperties => ({
  borderRadius: 22,
  padding: '0.95rem 1rem',
  border: active ? '1px solid rgba(56, 189, 248, 0.28)' : '1px solid rgba(148, 163, 184, 0.18)',
  background: active
    ? 'linear-gradient(135deg, #2563eb 0%, var(--htd-accent) 100%)'
    : 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(226,232,240,0.92) 100%)',
  boxShadow: active ? '0 16px 30px rgba(37, 99, 235, 0.24)' : '0 10px 24px rgba(15, 23, 42, 0.08)',
})

/** หน้าแจ้งฝากเงิน (หลังกดจองการโอนเงินผ่านระบบ) — แปลงจาก deposit-timeout.html */
const ModernDepositTimeoutPage: React.FC<ModernDepositTimeoutPageProps> = ({ themeConfig, assetConfig }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const stateAmount = (location.state as { amount?: string } | null)?.amount ?? ''
  const stateSenderAccount = (location.state as { senderAccount?: PendingDepositSenderAccount } | null)?.senderAccount
  const stateDepositAccount = (location.state as { depositAccount?: PublicDepositAccount } | null)?.depositAccount

  const [userBankAccounts, setUserBankAccounts] = useState<authService.UserBankAccount[]>([])
  const [depositAccounts, setDepositAccounts] = useState<PublicDepositAccount[]>([])
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  const [reservationId, setReservationId] = useState<string | null>(null)
  const [restoredAmount, setRestoredAmount] = useState<string>('') // จำนวนที่โหลดกลับจาก sessionStorage หลัง reload
  const [restoredRequestedAmount, setRestoredRequestedAmount] = useState<string>('')
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [userSummary, setUserSummary] = useState<authService.UserInfoSummary | null>(null)
  const reservationCreatedRef = useRef(false)
  const timeoutHandledRef = useRef(false)
  const tenantId = themeConfig.tenantId || ''
  const currentUser = authService.getCurrentUser()
  const userId = currentUser?.id || ''

  const themeFolder = useMemo(() => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue', [themeConfig.themeId])
  const getAssetUrl = useCallback((path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }, [themeFolder, assetConfig.CDN_BASE_URL])

  useEffect(() => {
    if (!authService.isAuthenticated()) return
    authService.fetchUserBankAccounts().then((list) => setUserBankAccounts(list ?? []))
  }, [])

  useEffect(() => {
    if (!authService.isAuthenticated()) return
    Promise.all([
      walletService.getWallet(),
      authService.fetchUserInfoSummary(),
    ]).then(([walletRes, summary]) => {
      if (walletRes.success && walletRes.data) setWalletData(walletRes.data)
      else setWalletData(null)
      setUserSummary(summary ?? null)
    })
  }, [])

  useEffect(() => {
    const tid = themeConfig.tenantId
    if (!tid) return
    getPublicDepositAccounts(tid).then(setDepositAccounts)
  }, [themeConfig.tenantId])

  // โหลดหรือสร้างการจอง: ถ้ามีการจอง pending ใน sessionStorage (รวมหลัง reload) ให้ใช้การจองนั้น ไม่สร้างใหม่
  useEffect(() => {
    if (!tenantId) return

    const tryRestore = (): Promise<boolean> => {
      const stored = getStoredReservation(tenantId)
      if (!stored?.reservationId) return Promise.resolve(false)
      const applyStored = () => {
        const expMs = new Date(stored.expiresAt).getTime()
        const left = Math.max(0, Math.floor((expMs - Date.now()) / 1000))
        setReservationId(stored.reservationId)
        setSecondsLeft(left)
        setRestoredAmount(String(stored.amount ?? ''))
        setRestoredRequestedAmount(String(stored.requestedAmount ?? Math.floor(Number(stored.amount ?? 0))))
        reservationCreatedRef.current = true
      }
      return getReservationStatus(stored.reservationId)
        .then((res) => {
          if (res.success && res.data) {
            const { status, expiresAt, amount } = res.data
            const expMs = expiresAt ? new Date(expiresAt).getTime() : 0
            if (status === 'pending' && expMs > Date.now()) {
              setReservationId(stored.reservationId)
              setSecondsLeft(Math.max(0, Math.floor((expMs - Date.now()) / 1000)))
              setRestoredAmount(String(amount ?? stored.amount ?? ''))
              setRestoredRequestedAmount(String(res.data.requestedAmount ?? stored.requestedAmount ?? Math.floor(Number(amount ?? stored.amount ?? 0))))
              reservationCreatedRef.current = true
              return true
            }
            clearStoredReservation(tenantId)
            return false
          }
          // API error หรือไม่มี data: ใช้ข้อมูลจาก storage แทน (ไม่ลบ ไม่สร้างซ้ำ)
          applyStored()
          return true
        })
        .catch(() => {
          // เน็ต/error: ใช้ข้อมูลจาก storage แทน เพื่อไม่ให้ไปสร้าง record ใหม่
          applyStored()
          return true
        })
    }

    // กรณีไม่มี stateAmount (reload): พยายาม restore ถ้าไม่มีหรือไม่ valid ให้ไป topup
    if (!stateAmount) {
      tryRestore()
        .then((restored) => {
          if (!restored) navigate('/topup', { replace: true })
        })
        .catch(() => navigate('/topup', { replace: true }))
      return
    }

    // กรณีมี stateAmount: ก่อนสร้างใหม่ต้องเช็ก storage ก่อน — ถ้ามีการจอง pending อยู่แล้ว ให้ใช้การจองนั้น (กันสร้างซ้ำตอน reload ที่ state ยังไม่หาย)
    const amountNum = parseInt(stateAmount, 10)
    if (!Number.isFinite(amountNum) || amountNum <= 0) return
    if (reservationCreatedRef.current) return
    reservationCreatedRef.current = true // กัน effect ถูกเรียกซ้ำ (Strict Mode / re-render) แล้วสร้างหลาย record

    tryRestore()
      .then((restored) => {
        if (restored) return // ใช้การจองเดิมแล้ว ไม่สร้างใหม่
        return Promise.all([
        getDecimalReservationCountdownMinutes(tenantId),
        authService.fetchUserBankAccounts(),
        getPublicDepositAccounts(tenantId),
      ]).then(([minutes, bankList, accounts]) => {
        const primary = stateSenderAccount || (Array.isArray(bankList) && bankList.length > 0 ? bankList[0] : null)
        const webAccount = stateDepositAccount || (
          Array.isArray(accounts)
            ? depositAccountService.resolveDepositAccountForAmount(accounts, amountNum)
            : null
        )
        if (!webAccount?.accountNumber) {
          reservationCreatedRef.current = false
          Swal.fire({
            icon: 'warning',
            title: 'ไม่พบบัญชีรับโอน',
            text: 'ระบบยังโหลดบัญชีเวปไม่สำเร็จ กรุณากลับไปหน้าฝากเงินแล้วลองใหม่อีกครั้ง',
          }).then(() => navigate('/topup', { replace: true }))
          return
        }
        setSecondsLeft(minutes * 60)
        createDecimalReservation(amountNum, {
          expiresInMinutes: minutes,
          depositorName: primary?.accountName ?? undefined,
          depositorBank: primary?.bankName || primary?.bankCode || undefined,
          depositorAccount: primary?.accountNumber ?? undefined,
          webAccountName: webAccount.accountName ?? undefined,
          webAccountNumber: webAccount.accountNumber ?? undefined,
          webBankName: webAccount.bankName ?? undefined,
        })
          .then((res) => {
            if (res.success && res.data) {
              setReservationId(res.data.reservationId)
              const expiresAt = new Date(res.data.expiresAt).getTime()
              const left = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
              setSecondsLeft(left)
              setRestoredAmount(String(res.data.amount))
              setRestoredRequestedAmount(String(res.data.requestedAmount ?? amountNum))
              setStoredReservation(tenantId, {
                reservationId: res.data.reservationId,
                expiresAt: res.data.expiresAt,
                amount: res.data.amount,
                requestedAmount: res.data.requestedAmount ?? amountNum,
                decimalSuffix: res.data.decimalSuffix,
              })
            }
          })
          .catch(() => {})
      })
    })
      .catch(() => {
        reservationCreatedRef.current = false
      })
  }, [navigate, stateAmount, stateDepositAccount, stateSenderAccount, tenantId])

  const primaryUserBank = useMemo(() => userBankAccounts[0] ?? null, [userBankAccounts])
  const displayAmount = restoredAmount || stateAmount || '0'
  const displayRequestedAmount = stateAmount || restoredRequestedAmount || displayAmount
  const toAccount = useMemo(
    () => depositAccountService.resolveDepositAccountForAmount(depositAccounts, parseFloat(displayRequestedAmount || '0')),
    [depositAccounts, displayRequestedAmount]
  )
  const amountDisplay = displayAmount && !Number.isNaN(Number(displayAmount))
    ? `${Number(displayAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท`
    : '-'
  const requestedAmountDisplay = displayRequestedAmount && !Number.isNaN(Number(displayRequestedAmount))
    ? `${Number(displayRequestedAmount).toLocaleString()} บาท`
    : '-'
  const balanceText =
    walletData != null
      ? `${walletService.formatCurrency(walletService.getDisplayBalance(walletData))} ฿`
      : '0.00 ฿'
  const pointsText = userSummary?.points != null ? `${Number(userSummary.points).toFixed(2)} pts` : '0.00 pts'
  const userBankSummaryText = primaryUserBank
    ? `${primaryUserBank.accountNumber} | ${primaryUserBank.bankName}`
    : 'ยังไม่ได้ผูกบัญชี'
  const draftId = reservationId ? `decimal-reservation:${reservationId}` : ''
  const stepLabels = ['เลือกยอดฝาก', 'โอนเข้าบัญชีธนาคาร', 'แนบสลิป']

  // นับถอยหลัง — หยุดที่ 0 ไม่นับติดลบ
  useEffect(() => {
    if (secondsLeft == null || secondsLeft <= 0) return
    const t = setInterval(() => setSecondsLeft((s) => (s == null || s <= 0 ? 0 : s - 1)), 1000)
    return () => clearInterval(t)
  }, [secondsLeft])

  // หมดเวลาแล้ว: แจ้งแล้วยกเลิกการจอง (backend อาจยกเลิกเองอยู่แล้ว) แล้วกลับไปหน้า ฝากเงิน
  useEffect(() => {
    if (secondsLeft !== 0 || timeoutHandledRef.current) return
    if (!reservationId) return
    timeoutHandledRef.current = true
    clearStoredReservation(tenantId)
    if (tenantId && userId && draftId) {
      pendingDepositDraftService.remove(tenantId, userId, draftId)
    }
    Swal.fire({
      toast: true,
      position: 'top',
      icon: 'info',
      title: 'หมดเวลาการจอง',
      timer: 2000,
      showConfirmButton: false,
    })
    cancelDecimalReservation(reservationId)
      .catch(() => {})
      .finally(() => navigate('/topup', { replace: true }))
  }, [draftId, navigate, reservationId, secondsLeft, tenantId, userId])

  const countdownDisplay = useMemo(() => {
    if (secondsLeft == null) return '--:--'
    const m = Math.floor(secondsLeft / 60)
    const s = secondsLeft % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }, [secondsLeft])

  const handleCopyToAccount = useCallback(() => {
    if (toAccount?.accountNumber) navigator.clipboard?.writeText(toAccount.accountNumber).catch(() => {})
  }, [toAccount?.accountNumber])

  const handleCancelReserve = useCallback(async () => {
    if (reservationId) {
      clearStoredReservation(tenantId)
      if (tenantId && userId && draftId) {
        pendingDepositDraftService.remove(tenantId, userId, draftId)
      }
      try {
        await cancelDecimalReservation(reservationId)
      } catch {
        // continue to navigate
      }
    }
    navigate('/topup', { replace: true })
  }, [draftId, navigate, reservationId, tenantId, userId])

  useEffect(() => {
    if (!tenantId || !userId || !draftId || !toAccount?.accountNumber) return
    pendingDepositDraftService.upsert(tenantId, userId, {
      id: draftId,
      amount: String(displayAmount),
      depositMethod: 'decimal_reservation',
      depositAccount: toAccount,
      usesPaymentGateway: false,
    })
  }, [displayAmount, draftId, tenantId, toAccount, userId])

  // WebSocket: เมื่อ admin ยกเลิกหรือยืนยันการจอง ให้แจ้ง user (SweetAlert แบบ modal ให้เห็นชัด)
  useEffect(() => {
    const handler = (e: CustomEvent<{ type?: string; reservationId?: string; cancelledBy?: string }>) => {
      const d = e.detail
      const eventReservationId = d?.reservationId != null ? String(d.reservationId) : ''
      const currentReservationId = reservationId != null ? String(reservationId) : ''
      if (!eventReservationId || eventReservationId !== currentReservationId) return
      if (d.type === 'reservation_cancelled' && d.cancelledBy === 'admin') {
        clearStoredReservation(themeConfig.tenantId || '')
        if (tenantId && userId && draftId) {
          pendingDepositDraftService.remove(tenantId, userId, draftId)
        }
        Swal.fire({
          icon: 'warning',
          title: 'การจองถูกยกเลิก',
          text: 'การจองฝากเงินถูกยกเลิกโดยผู้ดูแลระบบ กรุณาทำรายการใหม่อีกครั้ง',
          confirmButtonText: 'ตกลง',
          allowOutsideClick: false,
        }).then(() => {
          navigate('/topup', { replace: true })
        })
      }
      if (d.type === 'reservation_confirmed') {
        clearStoredReservation(themeConfig.tenantId || '')
        if (tenantId && userId && draftId) {
          pendingDepositDraftService.remove(tenantId, userId, draftId)
        }
        Swal.fire({
          icon: 'success',
          title: 'ฝากเงินสำเร็จ',
          text: 'ระบบได้ยืนยันการฝากเงินของคุณแล้ว',
          confirmButtonText: 'ตกลง',
        }).then(() => {
          navigate('/topup', { replace: true })
        })
      }
    }
    window.addEventListener('decimal_deposit_updated', handler as EventListener)
    return () => window.removeEventListener('decimal_deposit_updated', handler as EventListener)
  }, [draftId, navigate, reservationId, tenantId, themeConfig.tenantId, userId])

  // Polling fallback: ถ้า WebSocket ไม่ถึง (หรือปิดอยู่) ยังแจ้ง user ได้เมื่อ admin ยกเลิก/ยืนยัน
  useEffect(() => {
    if (!reservationId) return
    const POLL_INTERVAL_MS = 15000 // 15 วินาที
    const t = setInterval(() => {
      getReservationStatus(reservationId).then((res) => {
        if (!res.success || !res.data) return
        const status = res.data.status
        if (status === 'cancelled') {
          clearStoredReservation(tenantId)
          if (tenantId && userId && draftId) {
            pendingDepositDraftService.remove(tenantId, userId, draftId)
          }
          Swal.fire({
            icon: 'warning',
            title: 'การจองถูกยกเลิก',
            text: 'การจองฝากเงินถูกยกเลิกโดยผู้ดูแลระบบ กรุณาทำรายการใหม่อีกครั้ง',
            confirmButtonText: 'ตกลง',
            allowOutsideClick: false,
          }).then(() => {
            navigate('/topup', { replace: true })
          })
        }
        if (status === 'completed') {
          clearStoredReservation(tenantId)
          if (tenantId && userId && draftId) {
            pendingDepositDraftService.remove(tenantId, userId, draftId)
          }
          Swal.fire({
            icon: 'success',
            title: 'ฝากเงินสำเร็จ',
            text: 'ระบบได้ยืนยันการฝากเงินของคุณแล้ว',
            confirmButtonText: 'ตกลง',
          }).then(() => {
            navigate('/topup', { replace: true })
          })
        }
      })
    }, POLL_INTERVAL_MS)
    return () => clearInterval(t)
  }, [draftId, navigate, reservationId, tenantId, userId])

  if (!stateAmount && !restoredAmount) return null

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="row mx__1 mt-3" style={{ rowGap: '1rem' }}>
              <DepositPageHeader
                balanceText={balanceText}
                pointsText={pointsText}
                userBankSummaryText={userBankSummaryText}
              />

              <div className="col-12 px-2">
                <div className="light_sd align-items-center bdc_primary p-3 p-md-4 border_30" style={panelStyle}>
                  <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                    <div>
                      <div className="small text-white-50 mb-1">ขั้นตอนที่ 2 จาก 3</div>
                      <h4 className="c_tertiary font_md mb-1">ฝากทศนิยม</h4>
                      <p className="text-white-50 mb-0">
                        ตรวจสอบบัญชีปลายทาง โอนตามยอดสุ่มทศนิยมภายในเวลาที่กำหนด แล้วไปขั้นตอนแนบสลิปเหมือนการฝากปกติ
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 px-2">
                <div className="border_30 px-2 px-md-3 py-2 py-md-3" style={innerSurfaceStyle}>
                  <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
                    {stepLabels.map((label, index) => (
                      <div key={label} className="col-12 col-md-4 px-1">
                        <div
                          className={`h-100 ${index === 1 ? 'c_tertiary' : 'c_primary'}`}
                          style={stepCardStyle(index === 1)}
                        >
                          <div
                            className="small mb-1"
                            style={index === 1 ? { color: 'rgba(255,255,255,0.82)' } : lightSurfaceLabelStyle}
                          >
                            Step {index + 1}
                          </div>
                          <div className="fw-bold">{label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-5 px-2">
                <div className="light_sd align-items-center bdc_primary p-3 p-md-4 border_30 h-100" style={panelStyle}>
                  <h5 className="c_tertiary font_md mb-3">ข้อมูลรายการฝาก</h5>
                  <div className="d-grid gap-3">
                    <div style={valueCardStyle}>
                      <div className="small mb-1" style={lightSurfaceLabelStyle}>จำนวนเงินที่ต้องโอน</div>
                      <div className="c_primary" style={{ fontSize: '1.3rem', fontWeight: 800 }}>{amountDisplay}</div>
                    </div>
                    <div style={valueCardStyle}>
                      <div className="small mb-1" style={lightSurfaceLabelStyle}>ยอดตั้งต้น</div>
                      <div style={{ ...lightSurfaceHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>{requestedAmountDisplay}</div>
                      <div className="small mt-2" style={lightSurfaceMutedTextStyle}>
                        ระบบสุ่มเลขทศนิยมให้อัตโนมัติเพื่อแยกรายการฝาก
                      </div>
                    </div>
                    <div style={valueCardStyle}>
                      <div className="small mb-1" style={lightSurfaceLabelStyle}>ช่องทางฝาก</div>
                      <div style={{ ...lightSurfaceHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>ฝากทศนิยม</div>
                    </div>
                    <div style={valueCardStyle}>
                      <div className="small mb-1" style={lightSurfaceLabelStyle}>บัญชีของคุณ</div>
                      <div style={{ ...lightSurfaceHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                        {primaryUserBank?.bankName || 'ยังไม่ได้ผูกบัญชี'}
                      </div>
                      <div className="mt-1" style={lightSurfaceMutedTextStyle}>{primaryUserBank?.accountName || '-'}</div>
                      <div className="mt-2" style={{ color: '#b45309', fontWeight: 800 }}>{primaryUserBank?.accountNumber || '-'}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-7 px-2">
                <div className="light_sd align-items-center bdc_primary p-3 p-md-4 border_30 h-100" style={panelStyle}>
                  <h5 className="c_tertiary font_md mb-3">บัญชีปลายทางของระบบ</h5>
                  <div className="d-grid gap-3">
                    <div style={valueCardStyle}>
                      <div className="small mb-1" style={lightSurfaceLabelStyle}>บัญชีธนาคารที่ต้องโอน</div>
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        {toAccount ? (
                          <img
                            src={getDepositAccountLogoUrl(toAccount, getAssetUrl)}
                            alt={toAccount.bankName}
                            className="img-fluid rounded-4 bg-white p-2"
                            style={{ width: 88, height: 88, objectFit: 'contain' }}
                          />
                        ) : null}
                        <div className="flex-grow-1">
                          <div style={{ ...lightSurfaceHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                            {toAccount?.bankName || '-'}
                          </div>
                          <div className="mt-1" style={lightSurfaceMutedTextStyle}>{toAccount?.accountName || '-'}</div>
                          <div className="mt-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                            <div style={{ color: '#b45309', fontWeight: 800 }}>{toAccount?.accountNumber || '-'}</div>
                            {toAccount?.accountNumber && (
                              <button
                                type="button"
                                className="border-0 px-3 py-2 d-inline-flex align-items-center gap-2"
                                style={{
                                  borderRadius: 999,
                                  background: 'rgba(37, 99, 235, 0.10)',
                                  color: '#1d4ed8',
                                  fontWeight: 700,
                                }}
                                onClick={handleCopyToAccount}
                              >
                                <i className="fa fa-copy" aria-hidden="true" />
                                <span>Copy</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div style={valueCardStyle}>
                      <div className="small mb-1" style={lightSurfaceLabelStyle}>เวลาที่เหลือสำหรับการจอง</div>
                      <div className="c_primary" style={{ fontSize: '1.6rem', fontWeight: 800 }}>{countdownDisplay}</div>
                      <div className="small mt-2" style={lightSurfaceMutedTextStyle}>
                        กรุณาโอนตามยอดนี้ภายในเวลาที่กำหนด แล้วแนบสลิปในขั้นตอนถัดไป
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              <div className="col-12 px-2">
                <div className="light_sd align-items-center bdc_primary p-3 border_30" style={panelStyle}>
                  <div className="d-flex flex-column flex-md-row gap-2">
                    <button
                      type="button"
                      className="link_pmr box_row justify-content-center font_rg bg_secondary border_30 w-100 border-0"
                      onClick={() => void handleCancelReserve()}
                    >
                      <div className="d-flex align-items-center c_secondary font_md">
                        <h5 className="mb-0">ยกเลิกการจอง</h5>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                      disabled={!draftId || !toAccount?.accountNumber}
                      onClick={() => {
                        if (!draftId || !toAccount?.accountNumber) return
                        navigate(`/topup/slip?draft=${encodeURIComponent(draftId)}`, {
                          state: {
                            amount: displayAmount,
                            depositAccount: toAccount,
                            depositMethod: 'decimal_reservation',
                            usesPaymentGateway: false,
                          },
                        })
                      }}
                    >
                      <div className="d-flex align-items-center c_tertiary font_md">
                        <h5 className="mb-0">ไปขั้นตอนแนบสลิป</h5>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModernDepositTimeoutPage
